'use client'

import { useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'
import { setUserId, clearUserId, setUserType } from '@/lib/analytics'

/**
 * Google Analytics User Identifier
 * 
 * Sets the GA4 user_id and user_type when a user authenticates, connecting their
 * anonymous visitor sessions with their authenticated sessions.
 * 
 * This enables:
 * - Cross-device tracking when user signs in on different devices
 * - Complete user journey analysis from first visit through conversion
 * - Attribution of pre-auth marketing touchpoints to post-auth behavior
 * - Segmentation between authenticated users and anonymous visitors
 */
export function GoogleAnalyticsIdentifier() {
  const { data: session, status } = useSession()
  const previousUserId = useRef<string | null>(null)
  const hasSetInitialUserType = useRef(false)

  useEffect(() => {
    // Wait for session to load
    if (status === 'loading') return

    const currentUserId = session?.user?.id || null

    // User signed in - set user ID and type
    if (currentUserId && currentUserId !== previousUserId.current) {
      setUserId(currentUserId)
      setUserType('authenticated')
      previousUserId.current = currentUserId
      console.log('[Analytics] GA4 user_id set:', currentUserId)
    }

    // User signed out - clear user ID and set as visitor
    if (!currentUserId && previousUserId.current) {
      clearUserId()
      setUserType('visitor')
      previousUserId.current = null
      console.log('[Analytics] GA4 user_id cleared, set as visitor')
    }

    // Set initial user type for unauthenticated visitors (first load)
    if (status === 'unauthenticated' && !hasSetInitialUserType.current) {
      setUserType('visitor')
      hasSetInitialUserType.current = true
      console.log('[Analytics] GA4 user_type set to visitor (initial)')
    }
  }, [session, status])

  // This is a tracking component, it doesn't render anything
  return null
}
